import CheckboxField from "@imminently/decisively-ui/CheckboxField";
import { useFormContext, useWatch } from "@imminently/decisively-ui/Form";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import SelectField from "@imminently/decisively-ui/SelectField";
import Tabs from "@imminently/decisively-ui/Tabs";
import TextField from "@imminently/decisively-ui/TextField";
import { useEffect, useState } from "react";

import { useIsProjectTechnicalWriter } from "@/auth/permissions";
import { GoalField } from "@/components/GoalField";
import { useTemplateAttributes } from "@/features/release/hooks/useTemplateAttributes";
import { AttributeTemplateField } from "@/ui/forms/attribute-template-field";
import { TriggerIntroduction, TriggerSectionTitle } from "@/features/triggers/components/TriggerGuidance";
import GoalMenu from "@/pages/models/release/GoalMenu";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import Field from "@imminently/decisively-ui/Field";
import { AttributeCommunicationProxyForm } from "./AttributeCommunicationProxyForm";
import { AttributeIntegrationForm } from "./AttributeIntegrationForm";
import { TriggerScheduling } from "./TriggerScheduling";

export const AttributeCommunicationForm = ({ scope }: { scope: string }) => {
  const [activeTab, setActiveTab] = useState("definition");
  const graph = useParsedGraph();
  const { control, setValue } = useFormContext<any>();
  const canEdit = useIsProjectTechnicalWriter();

  const methodOptions = [
    { value: "email", label: "Email" },
    { value: "sms", label: "SMS" },
    // { value: "proxy", label: "Proxy" } // Disabled
  ];

  const scopedValue = (name: string) => `${scope}.${name}`;

  const selectedMethod = (useWatch<any>({ control, name: scopedValue("method") }) ?? "") as string;
  const isProxy = selectedMethod === "proxy";
  // Email bodies are HTML; SMS bodies (and every subject) are plain text.
  const isSms = selectedMethod === "sms";
  const templateAttributes = useTemplateAttributes(graph);

  const whoMethod = (useWatch<any>({ control, name: scopedValue("whoMethod") }) ?? "attribute") as
    | "attribute"
    | "integration"
    | "orgmodel";
  const isIntegrationWho = whoMethod === "integration";
  const isOrgModelWho = whoMethod === "orgmodel";
  const orgModelSubjectId = String(useWatch<any>({ control, name: scopedValue("whoOrgModel.subjectId") }) ?? "");
  const orgModelLevel = String(useWatch<any>({ control, name: scopedValue("whoOrgModel.level") }) ?? "");

  const whoMethodOptions = [
    { value: "attribute", label: "Attribute (from rule base)" },
    { value: "integration", label: "Integration (API call)" },
    { value: "orgmodel", label: "Org Models" },
  ];

  useEffect(() => {
    if (whoMethod === "attribute") {
      setValue(scopedValue("whoIntegration"), undefined);
      setValue(scopedValue("whoOrgModel"), undefined);
    } else if (whoMethod === "integration") {
      setValue(scopedValue("whoOrgModel"), undefined);
    } else if (whoMethod === "orgmodel") {
      setValue(scopedValue("whoIntegration"), undefined);
      setValue(scopedValue("whoIsContact"), true);
    }
  }, [whoMethod, scopedValue, setValue]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className="flex flex-col gap-4"
    >
      <TriggerIntroduction type="comms" />
      <Tabs.List>
        <Tabs.Trigger value="definition">Definition</Tabs.Trigger>
        <Tabs.Trigger value="recipient">Recipient</Tabs.Trigger>
      </Tabs.List>

      {/* Definition Tab */}
      <Tabs.Content
        value="definition"
        className="flex flex-col gap-4 mt-4"
      >
        <FormPanel>
          <TriggerSectionTitle>Delivery</TriggerSectionTitle>
          <FormPanel>
            <AttributeTemplateField
              label="Title"
              required
              name={scopedValue("title")}
              disabled={!canEdit}
              attributes={templateAttributes.attributes}
              groupOrder={templateAttributes.groupOrder}
              format="text"
              singleLine
              compact
            />
            <SelectField
              label="Method"
              required
              name={scopedValue("method")}
              options={methodOptions}
              disabled={!canEdit}
              fullWidth
            />
          </FormPanel>
        </FormPanel>

        {isProxy && (
          <FormPanel>
            <TriggerSectionTitle>Proxy Settings</TriggerSectionTitle>
            <FormPanel>
              <AttributeCommunicationProxyForm
                scope={scope}
                readOnly={!canEdit}
              />
            </FormPanel>
          </FormPanel>
        )}

        <FormPanel>
          <TriggerSectionTitle>Message</TriggerSectionTitle>
          <FormPanel>
            <AttributeTemplateField
              label="Subject"
              name={scopedValue("subject")}
              required
              disabled={!canEdit}
              attributes={templateAttributes.attributes}
              groupOrder={templateAttributes.groupOrder}
              format="text"
              singleLine
              compact
            />
            {/* Keyed on the method so the body remounts in the right format when it changes. */}
            <AttributeTemplateField
              key={`body-${isSms ? "sms" : "email"}`}
              label="Body"
              name={scopedValue("body")}
              disabled={!canEdit}
              attributes={templateAttributes.attributes}
              groupOrder={templateAttributes.groupOrder}
              format={isSms ? "text" : "html"}
            />
          </FormPanel>
        </FormPanel>

        <FormPanel>
          <TriggerSectionTitle>Scheduling</TriggerSectionTitle>
          <FormPanel>
            <TriggerScheduling
              scope={scope}
              disabled={!canEdit}
            />
          </FormPanel>
        </FormPanel>
      </Tabs.Content>

      {/* Recipient Tab */}
      <Tabs.Content
        value="recipient"
        className="flex flex-col gap-4 mt-4"
      >
        <FormPanel>
          <TriggerSectionTitle>Recipient</TriggerSectionTitle>
          <FormPanel>
            <SelectField
              label="Who Method"
              required
              name={scopedValue("whoMethod")}
              options={whoMethodOptions}
              disabled={!canEdit}
              fullWidth
            />

            {isIntegrationWho ? (
              <AttributeIntegrationForm
                scope={scopedValue("whoIntegration")}
                showTitle={false}
                showScheduling={false}
                mappingType="assignment"
              />
            ) : isOrgModelWho ? (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-muted-foreground">
                  Resolve recipients from Org Models configured under{" "}
                  <a
                    href="/monitor/contacts/org-models"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Monitor -&gt; Contacts -&gt; Org Models
                  </a>
                  . Subject type selects the relationship hierarchy to use (for example project or position), subject ID
                  identifies which subject to resolve at runtime, and level identifies which escalation level to read.
                </p>

                <TextField
                  label="Subject Type"
                  name={scopedValue("whoOrgModel.subjectType")}
                  disabled={!canEdit}
                  fullWidth
                />

                <Field fullWidth>
                  <Field.Label>Subject ID</Field.Label>
                  <Field.Control>
                    <GoalMenu
                      graph={graph}
                      showIdentifiers={true}
                      includeIdentifiers={true}
                      goalId={orgModelSubjectId}
                      onGoalIdChange={(value: string) => setValue(scopedValue("whoOrgModel.subjectId"), value)}
                      allowEmpty
                      disabled={!canEdit}
                    />
                  </Field.Control>
                </Field>

                <Field fullWidth>
                  <Field.Label>Level</Field.Label>
                  <Field.Control>
                    <GoalMenu
                      graph={graph}
                      showIdentifiers={true}
                      includeIdentifiers={true}
                      goalId={orgModelLevel}
                      onGoalIdChange={(value: string) => setValue(scopedValue("whoOrgModel.level"), value)}
                      allowEmpty
                      disabled={!canEdit}
                    />
                  </Field.Control>
                </Field>

                <p className="text-sm text-muted-foreground">
                  Use model logic to change level over time or by condition as needed.
                </p>
              </div>
            ) : (
              <GoalField
                required
                label="Who"
                name={scopedValue("who")}
                disabled={!canEdit}
              />
            )}

            {!isOrgModelWho && (
              <CheckboxField
                label="Who is Contact ID (if checked, we'll fetch email/phone from contact record)"
                name={scopedValue("whoIsContact")}
                disabled={!canEdit}
              />
            )}
          </FormPanel>
        </FormPanel>
      </Tabs.Content>
    </Tabs>
  );
};
